import { IJsonWebToken } from '@fulcrum/lib';

/**
 * Configuration interface for Trimble Identity services
 */
export interface IConfiguration {
  /** The connect provider URI. */
  connectProvider: string;

  /** The identity provider URI. */
  identityProvider: string;

  /** The identity client application name. */
  identityClientApp: string;

  /** The identity client id. */
  identityClientId: string;

  /** The identity client key (aka client secret). (client secret is not needed for pkce code-verifier and code-challenge approach)*/
  identityClientKey?: string;

  /** The identity client redirect URI. */
  identityRedirectUri: string;

  /** The identity client sign out redirect URI. */
  identitySignOutRedirectUri?: string;

  /** The preconfigured auth grants. */
  authGrants?: IAuthGrants;

  /**
   * If true, operates in memory-only mode:
   * - No browser storage (localStorage/sessionStorage)
   * - No multi-tab coordination
   * - Sessions lost on page refresh
   * - Each tab manages tokens independently
   */
  memoryOnly?: boolean;
}

/**
 * Identity token interface extending JWT
 */
export interface IIdentityToken extends IJsonWebToken {
  azp: string;
  at_hash: string;
  auth_time: number;
  identity_type: string;
  usertype: string;
  given_name: string;
  family_name: string;
  email_verified: string;
  email: string;
}

/**
 * Authentication grants interface
 */
export interface IAuthGrants {
  scope?: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  id_token?: string;
  access_token?: string;
}

/**
 * Session interface
 */
export interface ISession {
  sessionToken?: string;
  refreshToken?: string;
  accessToken?: string;
  expiresAt?: number;
  user?: IUser;
}

/**
 * User interface extending JWT
 */
export interface IUser extends IJsonWebToken {
  given_name: string;
  family_name: string;
  email_verified: string;
  email: string;
}

/**
 * Redirect interface
 */
export interface IRedirect {
  uri: string;
}

/**
 * Session storage target type
 */
export type SessionStorageTarget = 'SESSION' | 'LOCAL';

/**
 * Session event types for observer pattern
 */
export enum SessionEventType {
  SESSION_SAVED = 'SESSION_SAVED',
  SESSION_CLEARED = 'SESSION_CLEARED',
  SESSION_REFRESHED = 'SESSION_REFRESHED',
  SESSION_REFRESH_SKIPPED = 'SESSION_REFRESH_SKIPPED',
  SESSION_REFRESH_FAILED = 'SESSION_REFRESH_FAILED',
  SESSION_REFRESH_RETRY = 'SESSION_REFRESH_RETRY',
  CONFIGURATION_CHANGED = 'CONFIGURATION_CHANGED',
  SIGN_IN = 'SIGN_IN',
  SIGN_OUT = 'SIGN_OUT',
  TOKEN_REVOKED = 'TOKEN_REVOKED',
}

/**
 * Refresh failure type - determines if retry is possible
 */
export type RefreshFailureType = 'temporary' | 'permanent';

/**
 * Session event payload
 */
export interface ISessionEvent {
  type: SessionEventType;
  session?: ISession;
  previousSession?: ISession;
  configuration?: IConfiguration;
  /** Reason for the event (e.g., why refresh was skipped or failed) */
  reason?: string;
  /** Error that caused the event (for failure events) */
  error?: Error;
  /** Failure type for refresh failures */
  failureType?: RefreshFailureType;
  /** Retry attempt number (for retry events) */
  retryAttempt?: number;
  /** Max retry attempts configured */
  maxRetries?: number;
}

/**
 * Observer callback function type
 */
export type SessionObserver = (event: ISessionEvent) => void;

/**
 * Storage adapter interface to abstract storage implementation.
 * All methods return Promises to support async operations (e.g., encryption).
 */
export interface IStorageAdapter {
  getItem(keys: string[]): Promise<string | null>;
  setItem(key: string[], value: string): Promise<void>;
  removeItem(keys: string[]): Promise<void>;
  clear(): Promise<void>;
}
